import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { Button, Spinner, cn } from '../../ui';
import { modelLabel } from '../../features/chat/chatModel';
import type { ConversationSummary } from '../../features/chat/types';

interface ConversationListProps {
  conversations: ConversationSummary[];
  activeId: string | null;
  loading: boolean;
  onNew: () => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
}

/**
 * Left rail listing saved conversations. Selecting one loads it; the trash icon
 * soft-deletes it. "New chat" clears the active conversation to start fresh.
 */
export function ConversationList({
  conversations,
  activeId,
  loading,
  onNew,
  onSelect,
  onRemove,
}: ConversationListProps) {
  return (
    <div className="flex w-64 shrink-0 flex-col rounded-2xl border border-surface-border bg-surface-1">
      <div className="flex items-center justify-between gap-2 border-b border-surface-border px-3 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Conversations
        </span>
        <Button size="sm" variant="secondary" onClick={onNew} leftIcon={<Plus className="h-3.5 w-3.5" />}>
          New
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner label="Loading conversations" />
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-zinc-600">
            No saved conversations yet. Start a new one to see it here.
          </p>
        ) : (
          <ul className="space-y-1">
            {conversations.map((c) => {
              const isActive = c.id === activeId;
              return (
                <li key={c.id}>
                  <div
                    className={cn(
                      'group flex items-center gap-2 rounded-xl border px-2.5 py-2 transition-colors',
                      isActive
                        ? 'border-accent/40 bg-accent-soft/40'
                        : 'border-transparent hover:border-surface-border hover:bg-surface-2',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(c.id)}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    >
                      <MessageSquare
                        className={cn(
                          'mt-0.5 h-3.5 w-3.5 shrink-0',
                          isActive ? 'text-accent-fg' : 'text-zinc-500',
                        )}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-medium text-zinc-200">
                          {c.title || 'Untitled'}
                        </span>
                        <span className="block truncate text-[10px] text-zinc-500">
                          {modelLabel(c.modelId)} · {c.messageCount}{' '}
                          {c.messageCount === 1 ? 'msg' : 'msgs'}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(c.id)}
                      aria-label={`Delete ${c.title || 'conversation'}`}
                      className="shrink-0 rounded-md p-1 text-zinc-600 opacity-0 transition-colors hover:bg-surface-3 hover:text-red-300 focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ConversationList;
